import { Fragment } from 'react'
import { Check, TriangleAlert } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { cn } from '@/lib/utils'

export interface StepperStep {
  /** Label rendered under the circle */
  label: string
  /** Shows a red badge on the circle when the step has validation errors */
  hasError?: boolean
}

interface StepperProps {
  steps: StepperStep[]
  /** 1-based index of the active step */
  currentStep: number
  /** When provided, already-completed steps become tap targets */
  onStepClick?: (step: number) => void
  className?: string
}

/**
 * Horizontal wizard stepper. Purely presentational — the caller owns the step
 * index. Upcoming steps are never tappable, so tapping can only go backwards
 * and never skips the caller's forward validation.
 */
export function Stepper({ steps, currentStep, onStepClick, className }: StepperProps) {
  return (
    <nav
      aria-label="Progress"
      // max-w keeps the connectors from stretching absurdly wide on desktop;
      // on a phone the row fills the drawer as the design intends.
      className={cn('mx-auto flex w-full max-w-md items-center gap-x-1 px-4 py-2', className)}
    >
      {steps.map((step, index) => {
        const stepNumber = index + 1
        const isComplete = stepNumber < currentStep
        const isCurrent = stepNumber === currentStep
        const canNavigate = !!onStepClick && isComplete

        const content = (
          <>
            <span className="relative">
              <span
                className={cn(
                  'flex size-5 items-center justify-center rounded-full text-[10px] font-semibold leading-4 transition-colors',
                  isCurrent
                    ? 'bg-content-brand-bold text-content-primary-inverse'
                    : isComplete
                      ? 'bg-content-brand-light text-content-primary-inverse'
                      : 'bg-surface-L2 text-content-primary-subtle'
                )}
              >
                {isComplete ? <Check className="size-2.5" /> : stepNumber}
              </span>
              {step.hasError && (
                <span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-content-negative-bold">
                  <TriangleAlert className="size-2.5 text-content-primary-white" />
                </span>
              )}
            </span>
            <span
              className={cn(
                'whitespace-nowrap text-xs font-semibold transition-colors',
                isCurrent ? 'text-content-brand-bold' : 'text-content-primary-subtle-mid'
              )}
            >
              {step.label}
            </span>
          </>
        )

        return (
          <Fragment key={step.label}>
            {index > 0 && (
              // 1px rule sitting on the 20px circle's midline (10px − half a pixel)
              <span aria-hidden className="mt-[9.5px] h-px min-w-1 flex-1 self-start bg-border-L3" />
            )}
            {canNavigate ? (
              <button
                type="button"
                onClick={() => {
                  hapticLight()
                  onStepClick(stepNumber)
                }}
                className="flex shrink-0 flex-col items-center justify-center gap-2 active:opacity-70"
              >
                {content}
              </button>
            ) : (
              <span
                aria-current={isCurrent ? 'step' : undefined}
                className="flex shrink-0 flex-col items-center justify-center gap-2"
              >
                {content}
              </span>
            )}
          </Fragment>
        )
      })}
    </nav>
  )
}
